import { useToastStore, type ToastPayload, type ToastVariant } from '@stores/toast.store';

type ShowArgs = Omit<ToastPayload, 'id' | 'variant'> & { variant?: ToastVariant };

/**
 * Global toast hook.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success({ title: 'Saved' });
 *   toast.error({ title: 'Oops', message: 'Network error — try again.' });
 *
 * Only one toast is visible at a time; calling show() while another is
 * active replaces it (spec rule 8).
 */
export function useToast(): {
  success: (a: ShowArgs) => number;
  error: (a: ShowArgs) => number;
  warning: (a: ShowArgs) => number;
  info: (a: ShowArgs) => number;
  show: (a: ShowArgs & { variant: ToastVariant }) => number;
  dismiss: () => void;
} {
  const show = useToastStore((s) => s.show);
  const dismiss = useToastStore((s) => s.dismiss);
  return {
    success: (a) => show({ variant: 'success', ...a }),
    error: (a) => show({ variant: 'error', ...a }),
    warning: (a) => show({ variant: 'warning', ...a }),
    info: (a) => show({ variant: 'info', ...a }),
    show,
    dismiss,
  };
}
